import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Tag, 
  Button, 
  Space, 
  Modal, 
  Badge, 
  Card, 
  Statistic, 
  Row, 
  Col, 
  Tooltip,
  message,
  Divider,
  Descriptions
} from 'antd';
import { 
  SyncOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  ExclamationCircleOutlined,
  WifiOutlined,
  DisconnectOutlined,
  ReloadOutlined,
  DeleteOutlined,
  ClearOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined
} from '@ant-design/icons';
import { queueService, QUEUE_STATUS } from '../services/QueueService';

const QueueMonitor = ({ onClose }) => {
  const [queueItems, setQueueItems] = useState([]);
  const [queueSummary, setQueueSummary] = useState({});
  const [processingStatus, setProcessingStatus] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(Date.now());
  const [isLoading, setIsLoading] = useState(false);

  // Function to refresh all data
  const refreshData = () => {
    try {
      const items = queueService.getQueueItems();
      const summary = queueService.getQueueSummary();
      const status = queueService.getProcessingStatus();
      
      // Force update by creating new arrays/objects
      setQueueItems([...items]);
      setQueueSummary({...summary});
      setProcessingStatus({...status});
      setLastUpdate(Date.now());
      
      // Debug logging
      console.log(`Queue Monitor Update: ${items.length} items, Processing: ${status.isActive}`);
    } catch (error) {
      console.error('Error refreshing queue data:', error);
    }
  };

  useEffect(() => {
    // Initial load
    refreshData();

    // Update queue items and status every 2 seconds (reduced from 1 second for better performance)
    const interval = setInterval(() => {
      refreshData();
    }, 2000);

    return () => clearInterval(interval);
  }, []);
  const handleRetry = (id) => {
    setIsLoading(true);
    if (queueService.retryItem(id)) {
      message.success('Item queued for retry');
      refreshData(); // Force immediate refresh
    } else {
      message.error('Cannot retry this item');
    }
    setIsLoading(false);
  };

  const handleRetryAllFailed = () => {
    setIsLoading(true);
    const retriedCount = queueService.retryAllFailed();
    if (retriedCount > 0) {
      message.success(`Retried ${retriedCount} items`);
      refreshData(); // Force immediate refresh
    } else {
      message.info('No retryable items found');
    }
    setIsLoading(false);
  };

  const handleClearCompleted = () => {
    setIsLoading(true);
    const removedCount = queueService.clearCompletedItems();
    message.success(`Cleared ${removedCount} completed items`);
    refreshData(); // Force immediate refresh
    setIsLoading(false);
  };

  const handleClearFailed = () => {
    setIsLoading(true);
    const removedCount = queueService.clearFailedItems();
    message.success(`Cleared ${removedCount} failed items`);
    refreshData(); // Force immediate refresh
    setIsLoading(false);
  };

  const handleClearAll = () => {
    Modal.confirm({
      title: 'Clear All Queue Items',
      content: 'Are you sure you want to clear all queue items? This action cannot be undone.',
      onOk: () => {
        setIsLoading(true);
        const removedCount = queueService.clearAllItems();
        message.success(`Cleared all ${removedCount} items`);
        refreshData(); // Force immediate refresh
        setIsLoading(false);
      }
    });
  };

  const handleManualRefresh = () => {
    setIsLoading(true);
    refreshData();
    message.success('Queue data refreshed');
    setTimeout(() => setIsLoading(false), 500);
  };

  const showErrorDetails = (item) => {
    setSelectedItem(item);
    setIsErrorModalVisible(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case QUEUE_STATUS.COMPLETED:
        return <CheckCircleOutlined style={{ color: 'green' }} />;
      case QUEUE_STATUS.FAILED:
        return <CloseCircleOutlined style={{ color: 'red' }} />;
      case QUEUE_STATUS.IN_PROGRESS:
        return <SyncOutlined spin style={{ color: 'orange' }} />;
      case QUEUE_STATUS.PAUSED:
        return <PauseCircleOutlined style={{ color: 'purple' }} />;
      default:
        return <ExclamationCircleOutlined style={{ color: 'blue' }} />;
    }
  };

  const getFailureTypeColor = (failureType) => {
    switch (failureType) {
      case 'network': return 'orange';
      case 'authentication': return 'red';
      case 'server': return 'volcano';
      case 'validation': return 'purple';
      default: return 'default';
    }
  };  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      fixed: 'left',
      render: (id) => (
        <Tooltip title={id}>
          <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>
            {id.substring(0, 8)}...
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status, record) => (
        <Space direction="vertical" size={0}>
          <Space size="small">
            {getStatusIcon(status)}
            <Tag 
              color={status === QUEUE_STATUS.COMPLETED ? 'green' : 
                     status === QUEUE_STATUS.FAILED ? 'red' : 
                     status === QUEUE_STATUS.IN_PROGRESS ? 'orange' :
                     status === QUEUE_STATUS.PAUSED ? 'purple' : 'blue'}
              style={{ fontSize: '9px', padding: '1px 3px', margin: 0 }}
            >
              {status.replace('_', ' ').substring(0, 6).toUpperCase()}
            </Tag>
          </Space>
        </Space>
      ),
    },
    {
      title: 'Action',
      dataIndex: ['payload', 'action'],
      key: 'actionType',
      width: 100,
      render: (action) => (
        <Tag color="blue" style={{ fontSize: '9px', padding: '1px 3px', margin: 0 }}>
          {action ? action.replace('update_', '').substring(0, 8) : 'legacy'}
        </Tag>
      ),
    },
    {
      title: 'Retries',
      key: 'retries',
      width: 70,
      render: (_, record) => (
        <div style={{ fontSize: '10px' }}>
          <div>{record.retryCount || 0}/{record.maxRetries || 3}</div>
          {record.nextRetryAt && (
            <div style={{ fontSize: '8px', color: 'gray' }}>
              {new Date(record.nextRetryAt).toLocaleTimeString().substring(0, 5)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'failureType',
      key: 'failureType',
      width: 80,
      render: (failureType) => 
        failureType ? (
          <Tag 
            color={getFailureTypeColor(failureType)}
            style={{ fontSize: '8px', padding: '1px 2px', margin: 0 }}
          >
            {failureType.substring(0, 4).toUpperCase()}
          </Tag>
        ) : null,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 80,
      render: (date) => (
        <Tooltip title={new Date(date).toLocaleString()}>
          <div style={{ fontSize: '9px' }}>
            <div>{new Date(date).toLocaleDateString().substring(0, 8)}</div>
            <div>{new Date(date).toLocaleTimeString().substring(0, 5)}</div>
          </div>
        </Tooltip>
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 80,
      render: (date) => date ? (
        <Tooltip title={new Date(date).toLocaleString()}>
          <div style={{ fontSize: '9px' }}>
            <div>{new Date(date).toLocaleDateString().substring(0, 8)}</div>
            <div>{new Date(date).toLocaleTimeString().substring(0, 5)}</div>
          </div>
        </Tooltip>
      ) : (
        <span style={{ color: '#ccc' }}>-</span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" direction="vertical">
          {record.status === QUEUE_STATUS.FAILED && record.retryCount < record.maxRetries && (
            <Button 
              type="primary" 
              size="small"
              style={{ fontSize: '9px', height: '22px', padding: '0 6px' }}
              icon={<ReloadOutlined />}
              onClick={() => handleRetry(record.id)}
            >
              Retry
            </Button>
          )}
          {record.error && (
            <Button 
              type="link" 
              size="small"
              style={{ fontSize: '9px', height: '18px', padding: '0 4px' }}
              icon={<ExclamationCircleOutlined />}
              onClick={() => showErrorDetails(record)}
            >
              Details
            </Button>
          )}
        </Space>
      ),
    },
  ];  return (
    <div style={{ 
      padding: '16px', 
      maxHeight: '85vh', 
      overflow: 'auto',
      backgroundColor: '#fafafa',
      borderRadius: '8px'
    }}><style>
        {`
          .queue-monitor-table .ant-table-thead > tr > th {
            font-size: 11px !important;
            padding: 6px 4px !important;
            text-align: center !important;
            font-weight: bold !important;
          }
          .queue-monitor-table .ant-table-tbody > tr > td {
            font-size: 10px !important;
            padding: 4px !important;
            vertical-align: top !important;
          }
          .queue-monitor-table .ant-table-cell {
            white-space: nowrap !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
          .queue-monitor-table .ant-tag {
            margin: 0 !important;
            line-height: 1.2 !important;
          }
          .queue-monitor-table .ant-pagination {
            margin: 16px 0 !important;
            text-align: center !important;
          }
          .queue-monitor-table .ant-pagination-options {
            display: flex !important;
            align-items: center !important;
            gap: 8px !important;
          }
          .queue-monitor-table .ant-pagination-options-size-changer {
            margin-right: 8px !important;
          }
          .queue-monitor-table .ant-pagination-options-quick-jumper {
            margin-left: 8px !important;
          }
          .queue-monitor-table .ant-pagination-total-text {
            margin-right: 8px !important;
            font-size: 12px !important;
          }
          .queue-monitor-table .ant-select-selector {
            height: 24px !important;
            line-height: 22px !important;
            font-size: 12px !important;
          }
          .queue-monitor-table .ant-pagination-jump-next,
          .queue-monitor-table .ant-pagination-jump-prev {
            display: inline-block !important;
          }
          .queue-monitor-table .ant-pagination-item {
            min-width: 24px !important;
            height: 24px !important;
            line-height: 22px !important;
            font-size: 12px !important;
          }
          .queue-monitor-table .ant-pagination-prev,
          .queue-monitor-table .ant-pagination-next {
            min-width: 24px !important;
            height: 24px !important;
            line-height: 22px !important;
          }
          @media (max-width: 768px) {
            .queue-monitor-table .ant-pagination {
              flex-wrap: wrap !important;
            }
            .queue-monitor-table .ant-pagination-options {
              flex-direction: column !important;
              align-items: flex-start !important;
              margin-top: 8px !important;
            }
          }
        `}
      </style>
      {/* Header with status indicators */}
      <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Network Status"
              value={processingStatus.isOnline ? 'Online' : 'Offline'}
              prefix={processingStatus.isOnline ? 
                <WifiOutlined style={{ color: 'green' }} /> : 
                <DisconnectOutlined style={{ color: 'red' }} />
              }
              valueStyle={{ 
                color: processingStatus.isOnline ? 'green' : 'red',
                fontSize: '16px'
              }}
            />
          </Card>
        </Col>        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Queue Processing"
              value={processingStatus.isActive ? 'Active' : 'Stopped'}
              prefix={processingStatus.isActive ? 
                <PlayCircleOutlined style={{ color: 'green' }} /> : 
                <PauseCircleOutlined style={{ color: 'red' }} />
              }
              valueStyle={{ 
                color: processingStatus.isActive ? 'green' : 'red',
                fontSize: '16px'
              }}
              suffix={processingStatus.isProcessing ? <SyncOutlined spin style={{ marginLeft: 8 }} /> : null}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Total Items"
              value={queueSummary.total || 0}
              valueStyle={{ fontSize: '16px' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="Failed Items"
              value={queueSummary.failed || 0}
              valueStyle={{ 
                color: (queueSummary.failed || 0) > 0 ? 'red' : 'green',
                fontSize: '16px'
              }}
            />
          </Card>        </Col>
      </Row>

      {/* Last update timestamp */}
      <div style={{ textAlign: 'center', marginBottom: '16px', color: '#666', fontSize: '12px' }}>
        Last updated: {new Date(lastUpdate).toLocaleTimeString()}
      </div>

      {/* Queue summary stats */}
      <Row gutter={[8, 8]} style={{ marginBottom: '20px' }}>
        <Col span={4}>
          <Badge count={queueSummary.queued || 0} showZero>
            <Tag color="blue">Queued</Tag>
          </Badge>
        </Col>
        <Col span={4}>
          <Badge count={queueSummary.inProgress || 0} showZero>
            <Tag color="orange">In Progress</Tag>
          </Badge>
        </Col>
        <Col span={4}>
          <Badge count={queueSummary.completed || 0} showZero>
            <Tag color="green">Completed</Tag>
          </Badge>
        </Col>
        <Col span={4}>
          <Badge count={queueSummary.failed || 0} showZero>
            <Tag color="red">Failed</Tag>
          </Badge>
        </Col>
        <Col span={4}>
          <Badge count={queueSummary.paused || 0} showZero>
            <Tag color="purple">Paused</Tag>
          </Badge>
        </Col>
        <Col span={4}>
          <Badge count={queueSummary.retryable || 0} showZero>
            <Tag color="gold">Retryable</Tag>
          </Badge>
        </Col>
      </Row>      {/* Bulk action buttons */}
      <Space style={{ marginBottom: '20px' }} wrap>
        <Button 
          type="default"
          icon={<ReloadOutlined />}
          onClick={handleManualRefresh}
          loading={isLoading}
        >
          Refresh Data
        </Button>
        <Button 
          type="primary"
          icon={<ReloadOutlined />}
          onClick={handleRetryAllFailed}
          disabled={!queueSummary.retryable}
          loading={isLoading}
        >
          Retry All Failed ({queueSummary.retryable || 0})
        </Button>
        <Button 
          icon={<ClearOutlined />}
          onClick={handleClearCompleted}
          disabled={!queueSummary.completed}
          loading={isLoading}
        >
          Clear Completed ({queueSummary.completed || 0})
        </Button>
        <Button 
          icon={<DeleteOutlined />}
          onClick={handleClearFailed}
          disabled={!queueSummary.failed}
          loading={isLoading}
        >
          Clear Failed ({queueSummary.failed || 0})
        </Button>
        <Button 
          danger
          icon={<DeleteOutlined />}
          onClick={handleClearAll}
          disabled={!queueSummary.total}
          loading={isLoading}
        >
          Clear All ({queueSummary.total || 0})
        </Button>
      </Space>

      <Divider />      {/* Queue items table */}
      <Table
        key={lastUpdate} // Force re-render when data updates
        dataSource={queueItems}
        columns={columns}
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={{ 
          pageSize: 15,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: ['10', '15', '25', '50'],
          size: 'small',
          position: ['bottomCenter'],
          hideOnSinglePage: false,
          responsive: true,
          showLessItems: true
        }}
        scroll={{ 
          x: 'max-content',
          y: 400 
        }}
        className="queue-monitor-table"
        tableLayout="fixed"
      />

      {/* Error Details Modal */}
      <Modal
        title="Error Details"
        open={isErrorModalVisible}
        onCancel={() => setIsErrorModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsErrorModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        {selectedItem && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Item ID">{selectedItem.id}</Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={selectedItem.status === QUEUE_STATUS.FAILED ? 'red' : 'blue'}>
                {selectedItem.status.toUpperCase()}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Failure Type">
              {selectedItem.failureType ? (
                <Tag color={getFailureTypeColor(selectedItem.failureType)}>
                  {selectedItem.failureType.toUpperCase()}
                </Tag>
              ) : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Retry Count">
              {selectedItem.retryCount || 0} / {selectedItem.maxRetries || 3}
            </Descriptions.Item>
            <Descriptions.Item label="Last Retry">
              {selectedItem.lastRetryAt ? new Date(selectedItem.lastRetryAt).toLocaleString() : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Next Retry">
              {selectedItem.nextRetryAt ? new Date(selectedItem.nextRetryAt).toLocaleString() : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Error Message">
              <div style={{ 
                backgroundColor: '#f5f5f5', 
                padding: '8px', 
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '12px',
                wordBreak: 'break-word'
              }}>
                {selectedItem.error || 'No error message available'}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="Payload">
              <div style={{ 
                backgroundColor: '#f5f5f5', 
                padding: '8px', 
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '12px',
                maxHeight: '200px',
                overflow: 'auto'
              }}>
                <pre>{JSON.stringify(selectedItem.payload, null, 2)}</pre>
              </div>
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default QueueMonitor;
